<template>
    <div class="poll-block">
        <!-- Free-form poll: nothing to tap, just a written answer. Only staff
             ever see what everyone else wrote (the breakdown endpoint is
             staff-only) — participants see the count and their own answer. -->
        <div v-if="isText">
            <div v-if="editing || !post.my_text">
                <b-input
                    v-model="textAnswer"
                    type="textarea"
                    rows="3"
                    :disabled="closed"
                    :placeholder="closed ? 'This poll is closed.' : 'Type your answer...'"
                />
                <div class="mt-2">
                    <b-button
                        size="is-small"
                        type="is-primary"
                        :disabled="closed || saving || !textAnswer.trim()"
                        @click="submitText"
                    >{{ post.my_text ? 'Save' : 'Submit' }}</b-button>
                    <b-button size="is-small" class="ml-2" v-if="post.my_text" @click="cancelEdit">Cancel</b-button>
                </div>
            </div>
            <div v-else class="my-answer">
                <div class="is-size-7 has-text-grey mb-1">Your answer</div>
                <div class="answer-body">{{ post.my_text }}</div>
                <a class="is-size-7" v-if="!closed" @click="startEdit"><i class="far fa-pencil mr-1"></i>Edit</a>
            </div>
        </div>

        <!-- Choice poll. Options are always tappable rows: before answering
             they read as choices, after answering they show WhatsApp-style
             result bars with your picks marked. -->
        <template v-else>
            <div
                class="poll-option"
                v-for="option in post.options"
                :key="option.id"
                :class="{'is-mine': isSelected(option.id), 'has-voted': showResults, 'is-locked': closed}"
                @click="toggle(option)"
            >
                <div class="bar" v-if="showResults" :style="{width: percent(option) + '%'}"></div>
                <div class="option-row">
                    <span class="option-label">
                        <i class="mr-2" :class="optionIcon(option)"></i>
                        {{ option.label }}
                    </span>
                    <span class="option-count" v-if="showResults">{{ option.votes }} · {{ percent(option) }}%</span>
                </div>
            </div>

            <!-- The write-in only counts once something is actually written,
                 so selecting "Other" leaves the previous answer standing until
                 this is saved. -->
            <div class="mt-2" v-if="writeInOpen">
                <b-field class="mb-1">
                    <b-input
                        v-model="otherText"
                        size="is-small"
                        placeholder="Tell us..."
                        expanded
                        @keyup.enter.native="submitOther"
                    />
                    <p class="control">
                        <b-button size="is-small" type="is-primary" :disabled="saving || !otherText.trim()" @click="submitOther">Save</b-button>
                    </p>
                </b-field>
            </div>
            <div class="is-size-7 has-text-grey mb-2" v-else-if="post.my_text">
                Your answer: <em>{{ post.my_text }}</em>
            </div>

            <p class="is-size-7 has-text-grey mt-1 mb-1" v-if="isMulti && !closed">{{ limitHint }}</p>
        </template>

        <div class="is-size-7 has-text-grey mt-2">
            {{ post.voter_count }} {{ isText ? 'response' : 'vote' }}{{ post.voter_count === 1 ? '' : 's' }}
            <span class="ml-3" v-if="closed"><i class="fas fa-lock mr-1"></i>Closed</span>
            <span class="ml-3" v-else-if="post.poll_closes_at">
                <i class="far fa-clock mr-1"></i>Closes {{ post.poll_closes_at | moment('MMM Do, h:mma') }}
            </span>
            <a class="ml-3" v-if="isStaff" @click="showBreakdown">
                <i class="fas fa-chart-bar mr-1"></i>View responses
            </a>
        </div>

        <b-modal v-model="breakdownOpen">
            <div class="card">
                <div class="card-header">
                    <p class="card-header-title"><i class="fal fa-chart-bar fa-fw mr-3"></i>Poll Responses</p>
                </div>
                <div class="card-content">
                    <div class="content mt-2 mb-2" v-if="breakdownLoading">
                        <h6 class="has-text-grey"><i class="fal fa-tennis-ball fa-spin mr-3"></i>Loading...</h6>
                    </div>
                    <div v-else>
                        <!-- Free-form: every written answer, attributed. -->
                        <div v-if="isText" class="mb-4">
                            <p class="has-text-grey is-italic is-size-7" v-if="!breakdown.length">No responses yet.</p>
                            <div v-for="(voter, i) in breakdown" :key="i" class="mb-3">
                                <div class="is-size-7 has-text-grey">{{ voterName(voter) }}</div>
                                <div class="answer-body">{{ voter.text_answer }}</div>
                            </div>
                        </div>

                        <!-- v-for lives on its own element throughout: Vue 2
                             gives v-for priority over v-if/v-else on the same
                             tag, which silently collapses the branches. -->
                        <template v-else>
                            <div v-for="option in post.options" :key="option.id" class="mb-4">
                                <h6 class="mb-2">{{ option.label }} <span class="has-text-grey">({{ votersFor(option.id).length }})</span></h6>
                                <p class="has-text-grey is-italic is-size-7" v-if="!votersFor(option.id).length">No votes</p>
                                <div v-else-if="option.is_other">
                                    <div v-for="(voter, i) in votersFor(option.id)" :key="i" class="mb-2">
                                        <div class="is-size-7 has-text-grey">{{ voterName(voter) }}</div>
                                        <div class="answer-body">{{ voter.text_answer }}</div>
                                    </div>
                                </div>
                                <div v-else>
                                    <b-tag v-for="(voter, i) in votersFor(option.id)" :key="i" class="mr-2 mb-1">
                                        {{ voterName(voter) }}
                                    </b-tag>
                                </div>
                            </div>
                        </template>

                        <!-- The reason staff open this at all: who still owes
                             an answer before the caterer gets called. -->
                        <div class="mt-5" v-if="nonresponders.length">
                            <h6 class="mb-2">Haven't answered <span class="has-text-grey">({{ nonresponders.length }})</span></h6>
                            <b-tag v-for="(person, i) in nonresponders" :key="i" type="is-warning" class="mr-2 mb-1">
                                {{ voterName(person) }}
                            </b-tag>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import {client as http} from '@/http_client';

    export default {
        name: 'PollBlock',
        props: {
            post: {type: Object, required: true},
            regId: {type: Number, required: true},
            isStaff: {type: Boolean, default: false}
        },
        data: function() {
            return {
                selected: [],
                otherText: '',
                textAnswer: '',
                editing: false,
                saving: false,
                breakdownOpen: false,
                breakdownLoading: false,
                breakdown: [],
                nonresponders: []
            };
        },
        computed: {
            // poll_type is the discriminator, not options.length — a free-form
            // poll has no options at all.
            isText: function() {
                return this.post.poll_type === 'text';
            },
            isMulti: function() {
                return this.post.poll_type === 'multi';
            },
            closed: function() {
                return !!this.post.poll_closes_at && new Date(this.post.poll_closes_at).getTime() <= Date.now();
            },
            // Staff see live results without answering; everyone else once
            // they have answered, or once the poll shuts.
            showResults: function() {
                return this.post.responded || this.isStaff || this.closed;
            },
            otherOption: function() {
                return (this.post.options || []).find(o => o.is_other);
            },
            writeInOpen: function() {
                return !this.closed && !!this.otherOption && this.isSelected(this.otherOption.id);
            },
            maxChoices: function() {
                return this.post.poll_max_choices || 0;
            },
            atLimit: function() {
                return this.maxChoices > 0 && this.selected.length >= this.maxChoices;
            },
            limitHint: function() {
                return this.maxChoices ? `Choose up to ${this.maxChoices}.` : 'Choose as many as you like.';
            }
        },
        methods: {
            // Tolerates the older response shape (scalar my_vote) in case a
            // cached bundle and a fresh API ever cross.
            serverVotes: function() {
                if (Array.isArray(this.post.my_votes)) return this.post.my_votes.slice();
                return this.post.my_vote != null ? [this.post.my_vote] : [];
            },
            syncFromPost: function() {
                this.selected = this.serverVotes();
                this.otherText = this.post.my_text || '';
                this.textAnswer = this.post.my_text || '';
                this.editing = false;
            },
            isSelected: function(optionId) {
                return this.selected.indexOf(optionId) !== -1;
            },
            isOtherId: function(optionId) {
                return !!this.otherOption && this.otherOption.id === optionId;
            },
            optionIcon: function(option) {
                if (this.isMulti) return this.isSelected(option.id) ? 'fas fa-check-square' : 'far fa-square';
                return this.isSelected(option.id) ? 'fas fa-check-circle' : 'far fa-circle';
            },
            percent: function(option) {
                if (!this.post.voter_count) return 0;
                return Math.round(option.votes / this.post.voter_count * 100);
            },
            toggle: function(option) {
                if (this.closed || this.saving) return;
                const wasSelected = this.isSelected(option.id);

                if (this.isMulti) {
                    if (!wasSelected && this.atLimit) return;
                    this.selected = wasSelected
                        ? this.selected.filter(id => id !== option.id)
                        : this.selected.concat([option.id]);
                } else {
                    // Re-tapping your own pick is a no-op, except on the
                    // write-in where it reopens the text for editing.
                    if (wasSelected && !option.is_other) return;
                    this.selected = [option.id];
                }

                // Selecting the write-in commits nothing until something is
                // written — the server rejects it, and an abandoned edit
                // shouldn't destroy the previous answer.
                if (option.is_other && this.isSelected(option.id) && !this.otherText.trim()) return;
                this.save();
            },
            submitOther: function() {
                if (this.otherText.trim()) this.save();
            },
            submitText: function() {
                if (this.textAnswer.trim()) this.save();
            },
            startEdit: function() {
                this.textAnswer = this.post.my_text || '';
                this.editing = true;
            },
            cancelEdit: function() {
                this.textAnswer = this.post.my_text || '';
                this.editing = false;
            },
            save: function() {
                if (this.saving) return;
                this.saving = true;

                const text = this.isText ? this.textAnswer.trim() : this.otherText.trim();
                // Drop the write-in from the submission while it has no text;
                // the rest of the selection still saves.
                const optionIds = this.isText
                    ? []
                    : this.selected.filter(id => !this.isOtherId(id) || text);

                http.post(
                    `/app/experience/${this.regId}/announcements/${this.post.id}/vote`,
                    {option_ids: optionIds, text},
                    {force: true}
                ).then(() => {
                    this.saving = false;
                    this.editing = false;
                    this.$emit('changed');
                }).catch(error => {
                    this.saving = false;
                    // The poll shut between render and tap.
                    if (error.response && error.response.status === 409) {
                        this.$notify({
                            group: 'notify',
                            type: 'error',
                            title: 'Poll Closed',
                            text: 'This poll is no longer accepting answers.'
                        });
                    }
                    this.$emit('changed');
                });
            },
            showBreakdown: function() {
                this.breakdownOpen = true;
                this.breakdownLoading = true;
                http.get(`/app/experience/${this.regId}/announcements/${this.post.id}/votes`, {force: true}).then(response => {
                    this.breakdown = response.data.votes;
                    this.nonresponders = response.data.nonresponders || [];
                    this.breakdownLoading = false;
                });
            },
            votersFor: function(optionId) {
                return this.breakdown.filter(v => v.option_id === optionId);
            },
            voterName: function(voter) {
                return voter.preferred_name || [voter.first_name, voter.last_name].filter(Boolean).join(' ') || 'Unknown';
            }
        },
        watch: {
            'post.id': {immediate: true, handler: 'syncFromPost'},
            // A refetch — our own save, or someone else's vote arriving over
            // the socket — is authoritative for what's selected.
            'post.my_votes': function() {
                this.selected = this.serverVotes();
            }
        }
    };
</script>

<style lang="scss" scoped>
    .poll-block {
        .poll-option {
            position: relative;
            border: 1px solid #ddd;
            border-radius: 0.4rem;
            margin-bottom: 0.4rem;
            cursor: pointer;
            overflow: hidden;

            &:hover {
                border-color: #628b21;
            }

            &.is-mine {
                border-color: #628b21;

                .option-label i {
                    color: #628b21;
                }
            }

            &.is-locked {
                cursor: default;

                &:hover {
                    border-color: #ddd;
                }

                &.is-mine:hover {
                    border-color: #628b21;
                }
            }

            .bar {
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                background-color: rgba(98, 139, 33, 0.15);
                transition: width 0.3s ease;
            }

            .option-row {
                position: relative;
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0.5rem 0.8rem;

                .option-count {
                    font-size: 0.8rem;
                    color: #7a7a7a;
                    white-space: nowrap;
                    margin-left: 0.8rem;
                }
            }
        }

        .answer-body {
            white-space: pre-wrap;
            word-break: break-word;
        }

        .my-answer {
            border-left: 3px solid #628b21;
            padding-left: 0.8rem;
        }
    }
</style>
